'use client';

import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { clearCustomerLocalData } from '@/lib/customer/signout-cleanup';
import { CHOW_URL } from '@/lib/site-url';

// Client bridge to Chow, the consumer product at chow.menuraq.com.
//
// Chow and its /api/customer-auth/* endpoints live on that host. Main-domain
// pages (the public menu, ordering, booking) surface the signed-in customer by
// making credentialed cross-origin requests to it. Both hosts share the
// registrable domain menuraq.com, so those requests are same-SITE even though
// they are cross-ORIGIN — the session cookie rides along under SameSite=Lax and
// third-party cookie blocking does not apply.
//
// THIS IS THE SSO MECHANISM: there is one session, host-scoped on Chow, and
// every Menuraq surface reads it from here. Signing in on Chow signs you in on
// the menu pages, and vice versa, without any cookie-domain widening.

const CHOW_HOST = new URL(CHOW_URL).host;

// Host resolution, in priority order:
//   1. NEXT_PUBLIC_CUSTOMER_HOST — an explicit override, still honoured.
//   2. Chow's canonical host — the product answer, so nothing depends on an env
//      var being remembered. This defaulting matters: the variable pointing at a
//      host the CSP did not allow is what turned every sign-in on the public menu
//      into "Network error. Please try again." with no request even dispatched.
//   3. Same-origin — only when the page is already being served from a Menuraq
//      host that is not the main domain (local dev, previews, tunnels), where a
//      cross-origin call to production would be wrong.
const CUSTOMER_HOST = (process.env.NEXT_PUBLIC_CUSTOMER_HOST || '').trim() || CHOW_HOST;

function resolveCustomerOrigin(): string {
  if (!CUSTOMER_HOST) return '';
  const origin = `https://${CUSTOMER_HOST}`;
  if (typeof window === 'undefined') return origin;
  const here = window.location.hostname.toLowerCase();
  // Anything that is not a real menuraq.com host is a dev/preview surface; keep
  // it same-origin so localhost never fires credentialed calls at production.
  if (!here.endsWith('menuraq.com')) return '';
  return origin;
}

export const CUSTOMER_ORIGIN = resolveCustomerOrigin();

/** Absolute URL for a customer-surface path in prod; same-origin path in dev. */
export function customerUrl(path: string): string {
  return CUSTOMER_ORIGIN ? `${CUSTOMER_ORIGIN}${path}` : path;
}

/**
 * Where to SEND a guest who wants the Chow app — as distinct from where to call
 * its API.
 *
 * `customerUrl` cannot be reused for this. It is the API base as well as the
 * navigation base, and it deliberately returns a bare same-origin path off
 * menuraq.com so that localhost never fires credentialed requests at
 * production. That is right for fetches and wrong for links: it turned "Customer
 * dashboard" into a link to `/`, the marketing homepage, on every dev and
 * preview build.
 *
 * Chow's in-repo mount path is `/chow` (see lib/site-url.ts — it is never a
 * canonical URL, but it is the only place Chow actually renders locally), so
 * that is the honest local equivalent of the Chow host.
 */
export function chowAppUrl(path = '/'): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  if (CUSTOMER_ORIGIN) return `${CUSTOMER_ORIGIN}${suffix}`;
  return suffix === '/' ? '/chow' : `/chow${suffix}`;
}

/** Credentialed fetch against the customer surface (always sends cookies). */
export function customerFetch(path: string, init?: RequestInit): Promise<Response> {
  return fetch(customerUrl(path), { ...init, credentials: 'include' });
}

/** True when this page IS the customer subdomain (passkeys, host-scoped flows). */
export function isCustomerOrigin(): boolean {
  if (typeof window === 'undefined') return false;
  return CUSTOMER_ORIGIN ? window.location.origin === CUSTOMER_ORIGIN : true;
}

export type CustomerSignInResult = {
  error?: string;
  code?: string;
  status?: number;
  ok?: boolean;
  url?: string | null;
};

export type CustomerSignInOptions = {
  callbackUrl?: string;
  redirect?: boolean;
  [key: string]: unknown;
};

// Providers whose callback is a plain XHR POST (returns JSON). OAuth providers
// ("google") use the /signin route, which answers with the provider's authorize
// URL that the browser then follows.
const CREDENTIAL_PROVIDERS = new Set(['customer-email-otp', 'customer-passkey', 'customer-phone-otp']);

/**
 * Sign in/up against the customer surface, targeting the subdomain even when
 * invoked from a main-domain page. Mirrors the Auth.js v5 client contract
 * (CSRF token + POST to /api/customer-auth/{callback|signin}/{provider}) so the
 * session cookie is created host-scoped on the Chow host regardless of which
 * origin initiated the flow. Same-origin in dev keeps localhost self-contained.
 *
 * The installed dependency is next-auth v4.24.15, whose App Router handler
 * returns JSON only when the form body carries `json=true` — the v5
 * `X-Auth-Return-Redirect` header is never consulted. Without the body flag the
 * callback answers 302 with an empty body and `res.json()` fails, which is how
 * every sign-in method (email/phone OTP, passkey, Google) surfaced as
 * "Sign-in failed." / "Invalid or expired code." The header is kept for
 * forward-compatibility with a future v5 upgrade; the body flag is what v4
 * actually reads.
 */
export async function customerSignIn(
  provider: string,
  options: CustomerSignInOptions = {},
): Promise<CustomerSignInResult> {
  const { callbackUrl, redirect = true, ...signInParams } = options;
  const redirectTo = callbackUrl ?? window.location.href;
  const base = customerUrl('/api/customer-auth');

  const csrfRes = await fetch(`${base}/csrf`, { credentials: 'include' });
  if (!csrfRes.ok) return { error: 'Sign-in could not be started.' };
  const csrfData = (await csrfRes.json().catch(() => null)) as { csrfToken?: string } | null;
  const csrfToken = csrfData?.csrfToken;
  if (!csrfToken) return { error: 'Sign-in could not be started.' };

  const endpoint = CREDENTIAL_PROVIDERS.has(provider) ? 'callback' : 'signin';
  const body = new URLSearchParams({ csrfToken, callbackUrl: redirectTo });
  for (const [key, value] of Object.entries(signInParams)) {
    if (value != null) body.set(key, String(value));
  }
  // v4's JSON trigger (see comment above). Set after the param loop so a caller
  // can never override it.
  body.set('json', 'true');

  const res = await fetch(`${base}/${endpoint}/${provider}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Auth-Return-Redirect': '1',
    },
    body,
    credentials: 'include',
  });

  const data = (await res.json().catch(() => null)) as { url?: string } | null;
  if (!data?.url) return { error: 'Sign-in failed.', status: res.status, ok: res.ok };

  if (redirect) {
    window.location.href = data.url;
    if (data.url.includes('#')) window.location.reload();
    return { ok: true, url: data.url };
  }

  let error: string | undefined;
  let code: string | undefined;
  try {
    const target = new URL(data.url, window.location.origin);
    error = target.searchParams.get('error') ?? undefined;
    code = target.searchParams.get('code') ?? undefined;
  } catch {
    // Relative success URL; nothing to parse.
  }
  return { error, code, status: res.status, ok: res.ok, url: error ? null : data.url };
}

export type CustomerSessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type CustomerSessionData = {
  user?: {
    id?: string;
    name?: string | null;
    email?: string | null;
    image?: string | null;
    phoneNumber?: string | null;
  } | null;
  expires?: string;
} | null;

/**
 * ONE session, one request, many consumers.
 *
 * This used to be per-call-site state: every `useCustomerSession()` held its own
 * `useState` and fired its own `fetch` from its own effect. The public menu mounts
 * six or seven of them — `MenuDisplayPage`, `CustomerAccountAffordance`,
 * `WalletMoment`, `RecurringOrderToggle`, `OrderReviewCard`, the Chow bridges — so a
 * single guest page load dispatched twelve `/api/customer-auth/session` requests to
 * another origin. StrictMode's intentional double-invoke of effects is what turned
 * seven into twelve. Twelve credentialed cross-origin requests on the critical path
 * of the first render is not a nit: it is the reason the menu felt slow to open, and
 * it is a cross-origin request per component rather than per page.
 *
 * The session is a fact about the browser, not about a component, so it lives in a
 * module-level store with subscriber fan-out. The first `useCustomerSession()` mounts
 * triggers the fetch; every later one — and every later re-render of any of them —
 * subscribes to the same snapshot and costs nothing. `refresh()` still genuinely
 * refetches, it just does it once and notifies everyone.
 *
 * `useSyncExternalStore` rather than a Context provider on purpose: these consumers
 * live in trees that are not all under one provider (the Chow bridges, the review
 * cards, `RecurringOrderToggle`), and a module-level store needs no ancestor to mount.
 */
type CustomerSessionState = {
  status: CustomerSessionStatus;
  data: CustomerSessionData;
};

const INITIAL_SESSION_STATE: CustomerSessionState = { status: 'loading', data: null };

let sessionState: CustomerSessionState = INITIAL_SESSION_STATE;
const sessionSubscribers = new Set<() => void>();
/** De-dupes concurrent `refresh()` calls, including StrictMode's double effect. */
let sessionInFlight: Promise<void> | null = null;
/**
 * Whether this page load has already asked. Consumers mount progressively as the
 * menu hydrates, so without this every late arrival re-read the session — the same
 * fan-out as before, just spread over time instead of one tick. Set by any load;
 * only `refresh()` deliberately re-reads.
 */
let sessionRequested = false;

function publishSession(next: CustomerSessionState) {
  sessionState = next;
  for (const notify of sessionSubscribers) notify();
}

function subscribeToSession(onChange: () => void) {
  sessionSubscribers.add(onChange);
  return () => {
    sessionSubscribers.delete(onChange);
  };
}

function readSession() {
  return sessionState;
}

async function loadCustomerSession() {
  if (sessionInFlight) return sessionInFlight;
  sessionRequested = true;
  sessionInFlight = (async () => {
    try {
      const res = await customerFetch('/api/customer-auth/session');
      if (!res.ok) {
        publishSession({ status: 'unauthenticated', data: null });
        return;
      }
      const session = (await res.json()) as { user?: unknown; expires?: string } | null;
      if (session?.user) {
        publishSession({ status: 'authenticated', data: session as CustomerSessionData });
      } else {
        publishSession({ status: 'unauthenticated', data: null });
      }
    } catch {
      publishSession({ status: 'unauthenticated', data: null });
    }
  })();
  try {
    await sessionInFlight;
  } finally {
    // Cleared on completion so a later `refresh()` genuinely re-reads. Held for the
    // duration so simultaneous callers share the one request.
    sessionInFlight = null;
  }
}

export function useCustomerSession() {
  const { status, data } = useSyncExternalStore(subscribeToSession, readSession, readSession);

  // Only the FIRST consumer to mount reads the session; later arrivals subscribe to
  // the snapshot. StrictMode's second mount is collapsed by the same flag, so the
  // dev double-invoke stops being a second network request too.
  useEffect(() => {
    if (sessionRequested) return;
    void loadCustomerSession();
  }, []);

  // Explicit and unconditional: this is the "something changed, go look again" path
  // (sign-in completion, a sign-out elsewhere on the page). It always refetches.
  const refresh = useCallback(() => loadCustomerSession(), []);

  const signIn = useCallback(() => {
    window.location.assign(customerUrl('/'));
  }, []);

  const signOut = useCallback(async () => {
    try {
      // NextAuth v4 refuses to clear the session cookie unless the POST carries
      // a CSRF token it can match against the `next-auth.csrf-token` cookie
      // (core/index.js: the signout branch returns `?csrf=true` and skips the
      // cookie cleanup when `csrfTokenVerified` is false). Without this the
      // request "succeeds" (2xx/redirect) but the cookie survives, so a reload
      // silently re-authenticates — the "I can't sign out" symptom. Fetch the
      // token first (which also sets the cookie), then send it in the body,
      // exactly as `customerSignIn` already does for credentials sign-in.
      const csrfRes = await customerFetch('/api/customer-auth/csrf', { credentials: 'include' });
      const csrfData = (await csrfRes.json().catch(() => null)) as { csrfToken?: string } | null;
      const body = new URLSearchParams();
      if (csrfData?.csrfToken) body.set('csrfToken', csrfData.csrfToken);
      await customerFetch('/api/customer-auth/signout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
        credentials: 'include',
      });
    } catch {
      // Cookie may still be cleared on a failed follow-up; local state is reset
      // regardless so the UI reflects sign-out immediately.
    }
    // Device-local customer artefacts (guest-order links, the You-panel cache,
    // assistant conversations) go with the session — the same contract the
    // assistant's conversation restore honours. Runs even when the network
    // sign-out failed: the local session is already gone either way.
    clearCustomerLocalData();
    publishSession({ status: 'unauthenticated', data: null });
  }, []);

  return { status, data, signIn, signOut, refresh, customerUrl, customerFetch };
}
